import { memo, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Building2, Search, Sparkles } from 'lucide-react';
import type { SelectOrganizationProps } from '@/types';
import { getInitials, isNewOrganization, normalizeOrg } from '@/lib';

function SelectOrganizationComponent({
  data,
  onClick,
  userName = 'Administrator',
}: SelectOrganizationProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const normalized = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((org) => ({ raw: org, n: normalizeOrg(org) }));
  }, [data]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return normalized;
    const q = searchQuery.toLowerCase();

    return normalized.filter(({ n }) => {
      return (
        n.displayName.toLowerCase().includes(q) ||
        n.schemaName.toLowerCase().includes(q) ||
        n.orgType.toLowerCase().includes(q) ||
        n.roles.join(' ').toLowerCase().includes(q)
      );
    });
  }, [normalized, searchQuery]);

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 ">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none" />
      <div className="absolute top-20 right-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-6xl relative z-10">
        <div className="mb-10 max-w-md mx-auto">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
            <Input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base border-2 focus:border-primary/50 shadow-sm focus:shadow-md transition-all rounded-xl"
            />
          </div>
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium text-primary">
            Choose Your Workspace
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {filtered.map(({ raw, n }) => (
            <div key={raw.id} className="h-full">
              <Card
                className="group relative cursor-pointer h-full overflow-hidden"
                onClick={() => onClick(n.schemaName)}
              >
                {/* <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" /> */}

                <CardContent className="p-7 relative z-10">
                  <div className="flex items-start justify-between mb-5">
                    <Avatar className="w-16 h-16 rounded-2xl ring-2 ring-background">
                      {n.logoUrl ? (
                        <AvatarImage
                          src={n.logoUrl || '/placeholder.svg'}
                          alt={n.displayName}
                        />
                      ) : null}
                      <AvatarFallback className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-bold text-xl">
                        {getInitials(n.displayName)}
                      </AvatarFallback>
                    </Avatar>

                    {isNewOrganization(n.created_on) && (
                      <Badge
                        variant="secondary"
                        className="bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 dark:from-green-950 dark:to-emerald-950 dark:text-green-400 border border-green-200 dark:border-green-900 shadow-sm animate-pulse font-semibold"
                      >
                        NEW
                      </Badge>
                    )}
                  </div>

                  <h3 className="font-bold text-xl mb-1.5 truncate group-hover:text-primary transition-colors">
                    {n.displayName}
                  </h3>
                  <p className="text-sm text-muted-foreground/80 mb-5 font-medium">
                    {n.orgType}
                  </p>

                  <div className="flex items-center gap-2.5 mb-5 p-3 rounded-xl bg-muted/50 group-hover:bg-muted transition-colors">
                    <Avatar className="w-7 h-7 ring-2 ring-background">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                        {getInitials(userName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-xs font-semibold">You</p>
                      <p className="text-xs text-muted-foreground font-medium">
                        {n.roleLabel}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 animate-in fade-in zoom-in-95 duration-500 break-all">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-muted/50 mb-5">
              <Building2 className="w-10 h-10 text-muted-foreground" />
            </div>
            <p className="text-lg text-muted-foreground font-medium">
              No organizations found matching &quot;{searchQuery}&quot;
            </p>
            <p className="text-sm text-muted-foreground/70 mt-2">
              Try adjusting your search terms
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export const SelectOrganization = memo(SelectOrganizationComponent);
